import React, { useState, useEffect } from "react";
import { useLocation, Route, Redirect, Switch } from "react-router-dom";
import Dashboard from "./components/Main_Page/mainHomepage";
import Login from "./Login/Login";
import PasswordReset from "./Login/passwordReset";
import Registration from "./registration/registration";
import MFAOtp from "./components/MFA/MFAOtp";
import { useHistory } from "react-router-dom";
import { BASE_URL } from "./App_Settings/app_settings";
import { useSharedValue } from "../src/components/Constants/constants";

// Protected Route Component
const ProtectedRoute = ({ component: Component, isLoggedIn, someValue, ...rest }) => (
  <Route
    {...rest}
    render={(props) =>
      isLoggedIn || someValue === true ? <Component {...props} /> : <Redirect to="/" />
    }
  />
);

const MainApp = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const history = useHistory();
  const { isDirectNavigation, updateDirectNavigation } = useSharedValue();
  let someValue = location.state ? location.state.someValue : false;

  // Check authentication status immediately on component mount
  useEffect(() => {
    const checkAuthentication = () => {
      const userDataJSON = localStorage.getItem("token");
      if (userDataJSON) {
        try {
          const userData = JSON.parse(userDataJSON);
          if (userData && userData.userId) {
            setIsLoggedIn(true);
          } else {
            setIsLoggedIn(false);
            localStorage.removeItem("token");
          }
        } catch (error) {
          console.error("Invalid token in localStorage:", error);
          setIsLoggedIn(false);
          localStorage.removeItem("token");
        }
      } else {
        setIsLoggedIn(false);
      }
      setIsLoading(false);
    };

    checkAuthentication();
  }, []);

  useEffect(() => {
    const isValidUUID = (id) => {
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return uuidRegex.test(id);
    };

    const getIdsFromUrl = () => {
      const url = window.location.href;
      const segments = url.split("/").filter((segment) => segment.trim() !== "");

      // Extract the last two segments as potential UUIDs
      const lastSegment = segments[segments.length - 1];
      const secondLastSegment = segments[segments.length - 2];

      // Check if we have two valid UUIDs at the end
      if (isValidUUID(lastSegment) && isValidUUID(secondLastSegment)) {
        return {
          organizationId: secondLastSegment,
          userId: lastSegment,
        };
      }

      // Fallback: check if only the last segment is a valid UUID (old format)
      if (isValidUUID(lastSegment)) {
        return {
          organizationId: null,
          userId: lastSegment,
        };
      }

      return { organizationId: null, userId: null };
    };

    const { organizationId, userId } = getIdsFromUrl();

    // Check if we have valid IDs from URL
    if (userId && userId.trim() !== "") {
      // Set direct navigation to true immediately for auto-login
      updateDirectNavigation(true);

      const validateUser = async () => {
        try {
          // Build API URL based on whether we have both IDs or just user ID
          const apiUrl = organizationId
            ? `${BASE_URL}/Users/validateuser/${organizationId}/${userId}`
            : `${BASE_URL}/Users/validateuser/${userId}`;

          const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          });

          if (!response.ok) {
            alert(`HTTP error! Status: ${response.status}`);
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const data = await response.json();

          if (data.userId !== undefined) {
            // Store the token from validateuser API response
            localStorage.setItem("token", JSON.stringify(data));

            const currentTime = new Date();
            localStorage.setItem("loginTime", currentTime);

            // Ensure direct navigation remains true
            updateDirectNavigation(true);
            setIsLoggedIn(true);

            // Always redirect to dashboard for auto-login
            history.push({
              pathname: "/dashboard",
              state: { someValue: true },
            });
          } else {
            alert("Invalid user credentials. Please try again.");
            // Reset direct navigation on failure
            updateDirectNavigation(false);
          }
        } catch (error) {
          console.error("Error during authentication:", error);
          alert("Authentication failed. Please try again.");
          // Reset direct navigation on error
          updateDirectNavigation(false);
        }
      };

      validateUser();
    } else {
      // console.log("No valid UUID in URL, skipping auto-login");
    }
  }, [history, updateDirectNavigation]); // Adding dependencies

  // Update authentication state when someValue changes
  useEffect(() => {
    if (someValue === true) {
      setIsLoggedIn(true);
    }
  }, [someValue]);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f5f5f5",
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <Switch>
      <Route exact path="/login" render={() => <Login />} />
      <Route exact path="/mfa-verify" render={(props) => <MFAOtp {...props} />} />
      <Route exact path="/homePage" render={() => <Dashboard />} />
      <Route exact path="/registration" component={() => <Registration />} />
      <Route exact path="/passwordReset" component={PasswordReset} />

      {/* Protected Routes - Redirect to root if not authenticated */}
      <ProtectedRoute
        path="/dashboard"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/workloadFileImport"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/bulkFormImport"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/displayAndEditFile"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/displayExtractionView"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/extractionReview"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/userRoleProfile"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/businessUnit"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/systemLink"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/organisation"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/export"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/approval"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/reports"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/approvalReviewProcess"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/home"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/rejections"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/extractionApproval"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/billing"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/userSettings"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />

      {/* Catch-all route - redirect any unmatched authenticated routes to dashboard, unauthenticated to root */}
      <Route
        render={() =>
          isLoggedIn || someValue === true ? <Redirect to="/dashboard" /> : <Redirect to="/" />
        }
      />
    </Switch>
  );
};

export default MainApp;
